import { create } from 'zustand';
import { PaymentMethodState } from './interfaces';
import { Console } from '@utils/logger';
import { Order, PaymentMethod } from '@interfaces';

const mekiCreditsChecked =
  typeof localStorage !== 'undefined'
    ? Boolean(localStorage.getItem('credits'))
    : false;

const usePaymentMethodStore = create<PaymentMethodState>()((set) => ({
  mekiCreditsChecked: mekiCreditsChecked,
  insuranceId: null,
  totalReimbursement: 0,
  totalDeductible: 0,
  totalCopay: 0,
  orders: [],
  ordersTotal: 0,
  paymentMethods: [],
  actions: {
    setMekiCreditsChecked: (to: boolean) => {
      try {
        if (typeof localStorage !== 'undefined') {
          if (to) {
            localStorage.setItem('credits', '1');
          } else {
            localStorage.removeItem('credits');
          }
        }
      } catch (error) {
        Console.warn(
          'Failed to set "credits" in localStorage in setMekiCreditsChecked:',
          error,
        );
      }
      set({ mekiCreditsChecked: to });
    },
    setInsuranceId: (to: number) => {
      set({ insuranceId: to });
    },
    setInsuranceTotalInfo: (info: {
      insuranceId: number;
      totalReimbursement: number;
      totalDeductible: number;
      totalCopay?: number;
    }) => {
      set({
        insuranceId: info.insuranceId,
        totalReimbursement: info.totalReimbursement,
        totalDeductible: info.totalDeductible,
        totalCopay: info.totalCopay,
      });
    },
    setOrders: (to: Order[]) => {
      set({ orders: to });
    },
    setOrdersTotal: (to: number) => {
      set({ ordersTotal: to });
    },
    setPaymentMethods: (to: PaymentMethod[]) => {
      set({ paymentMethods: to });
    },
    cleanValues: () => {
      try {
        if (typeof localStorage !== 'undefined') {
          localStorage.removeItem('credits');
        }
      } catch (error) {
        Console.warn(
          'Failed to remove "credits" from localStorage in cleanValues:',
          error,
        );
      }
      set({
        mekiCreditsChecked: false,
        insuranceId: null,
        totalReimbursement: 0,
        totalDeductible: 0,
        totalCopay: 0,
        orders: [],
        ordersTotal: 0,
        paymentMethods: [],
      });
    },
  },
}));

export const usePaymentSelectors = () =>
  usePaymentMethodStore((state) => {
    return {
      mekiCreditsChecked: state.mekiCreditsChecked,
      insuranceId: state.insuranceId,
      totalReimbursement: state.totalReimbursement,
      totalDeductible: state.totalDeductible,
      totalCopay: state.totalCopay,
    };
  });

export const useOrderAndPaymentInfo = () =>
  usePaymentMethodStore((state) => {
    return {
      orders: state.orders,
      ordersTotal: state.ordersTotal,
      paymentMethods: state.paymentMethods,
    };
  });

export const usePaymentMethodActions = () =>
  usePaymentMethodStore((state) => state.actions);
